/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-underscore-dangle */
import { store } from 'redux/store';
import { useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';

import { updateShare } from 'modules/partners/redux/sharesActions';
import { addPartner } from 'modules/partners/redux/partnerActions';
import { checkPartnerExists } from 'modules/partners/api/checkPartnerExists';

import documentTypes from 'constants/documentTypes';
import fileTypes from 'constants/fileTypes';

import cleanText from 'utils/cleanText';
import successSwal from 'utils/successSwal';
import { generateEmail } from 'utils/filters';
import UpdateSharesBody from './components/UpdateSharesBody';
import UpdateSharesHeader from './components/UpdateSharesHeader';
import UpdateSharesFooter from './components/UpdateSharesFooter';

import './UpdateShares.scss';
import { EncumbranceTypes } from './constants/encumbranceOptions';
import { encumbranceCoOwnersConfig } from './constants/encumbranceCoOwnersConfig';

const UpdateShares = ({
  share,
  society,
  isReadOnly,
  fetchPartnerData,
  ...props
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const actualSociety = useSelector((state) => state.society?.actualSociety);

  const partner = share?.partner;

  const [page, setPage] = useState(0);

  const [tag, setTag] = useState(share?.tag || '');

  const [encumbrances, setEncumbrances] = useState(share?.encumbrances || []);

  const [encumbrancesUpdated, setEncumbrancesUpdated] = useState(false);
  const [tagUpdated, setTagUpdated] = useState(false);

  const addEncumbrancesDocuments = async (encumbrances, partnerId) => {
    const result = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const encumbrance of encumbrances) {
      const currentEncumbrance = {
        name: encumbrance.name,
        date: encumbrance.date,
        from: encumbrance.from,
        to: encumbrance.to,
        scriptureData: encumbrance.scriptureData,
        scriptureDocument: encumbrance.scriptureDocument,
        coOwners: encumbrance.coOwners,
      };
      if (encumbrance?.file) {
        let uploadedDocument = null;
        const encumbranceFile = encumbrance.file;

        const partnersFromCoOwners = encumbrance.coOwners?.map(
          (coOwner) => coOwner.partner
        );
        const linkedTo = [partnerId, ...partnersFromCoOwners]?.map(
          (partner) => ({
            linkedId: partner,
            linkedType: 'PARTNER',
          })
        );

        const newDocument = {
          file: encumbranceFile,
          date: new Date(),
          size: encumbranceFile.size,
          name: encumbranceFile.name,
          author: user['_id'],
          society: society['_id'],
          fileType: fileTypes[encumbranceFile.type],
          category: documentTypes.SCRIPTURES,
          description: t('DocumentLinkedToEncumbrance'),
          linkedTo,
        };
        // eslint-disable-next-line no-await-in-loop
        uploadedDocument = await store.dispatch(addDocument(newDocument));

        if (uploadedDocument) {
          result.push({
            ...currentEncumbrance,
            scriptureDocument: uploadedDocument['_id'],
          });
        }
      } else {
        result.push({
          ...currentEncumbrance,
        });
      }
    }
    return result;
  };

  const addEncumbrancesPartners = async (encumbrances) => {
    const partnersToUpdate = [];

    const encumbrancesForNewPartners = [
      EncumbranceTypes.CO_OWNERSHIP,
      EncumbranceTypes.USUFRUCT,
    ];

    const newEncumbrances = encumbrances.filter((encumbrance) =>
      encumbrancesForNewPartners.includes(encumbrance.name)
    );

    const newPartners = [];

    newEncumbrances.forEach((encumbrance) => {
      encumbrance.coOwners.forEach((coOwner) => {
        if (coOwner.partner === null) {
          newPartners.push({
            ...coOwner,
            encumbranceName: encumbrance.name,
            encumbranceDate: encumbrance.date,
          });
        }
      });
    });

    if (newPartners.length === 0) {
      return;
    }

    await Promise.all(
      newPartners.map(async (partner) => {
        const { name, cif, email, encumbranceName, encumbranceDate } = partner;

        // TODO: Move to the backend
        const partnerExists = await store.dispatch(
          checkPartnerExists(actualSociety?.['_id'], cif)
        );

        if (partnerExists) {
          return;
        }

        const partnerData = {
          name: cleanText(name),
          email: email ? cleanText(email) : generateEmail(actualSociety),
          date: new Date(),
          society: actualSociety._id,
          societyName: actualSociety.name,
          userEmail: user.email,
          cif: cleanText(cif),
          user: user['_id'],
          isNaturalPerson: true, // [AXA]: TODO: check with @Marti if this is correct
          incorporationDate: new Date(encumbranceDate),
        };

        if (partner?.encumbranceName === EncumbranceTypes.CO_OWNERSHIP) {
          partnerData.categories = [
            encumbranceCoOwnersConfig[encumbranceName].categoryId,
          ];
        }

        const partnerUploaded = await store.dispatch(addPartner(partnerData));

        if (partnerUploaded) {
          partnersToUpdate.push({
            ...partner,
            partner: partnerUploaded['_id'],
          });
        }
      })
    );

    successSwal(t('PartnerAddedSuccessfully'));

    return partnersToUpdate;
  };

  const updateCoOwnerPartner = (partner, updatedPartners) => {
    const currentCif = partner.cif;
    const updatedPartnerWithCif = updatedPartners.find(
      (updatedPartner) => updatedPartner.cif === currentCif
    );

    if (updatedPartnerWithCif) {
      const newPartnerId = updatedPartnerWithCif.partner;
      return {
        ...partner,
        partner: newPartnerId,
      };
    }

    return { ...partner };
  };

  const updateEncumbrancesWithPartners = ({ encumbrances, partners }) => {
    if (!partners?.length) {
      return encumbrances;
    }
    const updatedEncumbrances = encumbrances.map((encumbrance) => {
      const encumbrancePartners = partners.filter(
        (partner) => partner.encumbranceName === encumbrance.name
      );

      if (encumbrancePartners.length > 0) {
        return {
          ...encumbrance,
          coOwners: encumbrance.coOwners.map((partner) =>
            updateCoOwnerPartner(partner, encumbrancePartners)
          ),
        };
      }

      return encumbrance;
    });

    return updatedEncumbrances;
  };

  const handleSaveShare = async () => {
    try {
      const newShare = { ...share };

      if (tagUpdated) {
        newShare.tag = tag;
      }

      if (encumbrancesUpdated) {
        const newEncumbrances = await addEncumbrancesDocuments(
          encumbrances,
          partner._id
        );
        const newPartners = await addEncumbrancesPartners(encumbrances);

        newShare.encumbrances = updateEncumbrancesWithPartners({
          encumbrances: newEncumbrances,
          partners: newPartners,
        });
        newShare.method = 'UPDATE_ENCUMBRANCES';
      }

      await store.dispatch(updateShare(newShare));

      if (fetchPartnerData) {
        await fetchPartnerData();
      }

      setEncumbrancesUpdated(false);
      setTagUpdated(false);
      dispatch(setModal(null));
    } catch (error) {
      throw new Error(error);
    }
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <UpdateSharesHeader
        handleCloseModal={handleCloseModal}
        isReadOnly={isReadOnly}
      />

      <UpdateSharesBody
        page={page}
        share={share}
        partner={partner}
        encumbrances={encumbrances}
        tag={tag}
        setEncumbrances={setEncumbrances}
        setEncumbrancesUpdated={setEncumbrancesUpdated}
        setTag={setTag}
        setTagUpdated={setTagUpdated}
        handleChangePage={handleChangePage}
        isReadOnly={isReadOnly}
      />

      <UpdateSharesFooter
        encumbrancesUpdated={encumbrancesUpdated}
        tagUpdated={tagUpdated}
        page={page}
        handleUpdateShare={handleSaveShare}
        handleCloseModal={handleCloseModal}
        isReadOnly={isReadOnly}
      />
    </>
  );
};

export default UpdateShares;
